import { z } from 'zod';

import type { ConnectedActivityMetadata } from './activity';

const toolkit = z.enum(['slack', 'googlesuper']);
const scope = z.enum(['personal', 'shared']);
const uuid = z.string().uuid();
export const IntegrationConnectionSchema = z.object({
  id: uuid,
  organization_id: uuid,
  user_id: uuid.nullable(),
  connected_by_user_id: uuid.nullable(),
  account_type: scope,
  toolkit_slug: toolkit,
  status: z.enum(['pending', 'active', 'failed', 'disconnected', 'expired']),
  status_changed_at: z.string(),
  account_label: z.string().nullable(),
  connected_at: z.string().nullable(),
  last_used_at: z.string().nullable(),
  created_at: z.string(),
});
export const IntegrationPolicySchema = z.object({
  organization_id: uuid,
  toolkit_slug: toolkit,
  enabled: z.boolean(),
  allowed_scopes: z.enum(['both', 'shared_only']),
});
export const IntegrationListSchema = z.object({
  toolkits: z.array(toolkit).max(2),
  policies: z.array(IntegrationPolicySchema).max(2),
  connections: z.array(IntegrationConnectionSchema).max(100),
  cleanup: z
    .array(
      z.object({
        id: uuid,
        cleanupPending: z.boolean(),
        cleanupFailed: z.boolean(),
      }),
    )
    .max(100),
  isApprover: z.boolean(),
  vendorIncident: z.boolean(),
  googlePendingVerification: z.boolean(),
  nextCursor: uuid.nullable(),
});
export const IntegrationConnectSchema = z.object({
  connectionId: uuid,
  redirectUrl: z.string().url(),
  correlation: z.string().regex(/^[\da-f]{16}$/),
});
export const IntegrationConfirmSchema = z.object({
  status: z.enum(['pending', 'active']),
});
export const IntegrationPurgeSchema = z.object({
  processed: z.number().int().nonnegative(),
  cleanupPending: z.number().int().nonnegative(),
  nextCursor: uuid.nullable(),
});
export type IntegrationConnection = z.infer<typeof IntegrationConnectionSchema>;
export type IntegrationPolicy = z.infer<typeof IntegrationPolicySchema>;
export type IntegrationList = z.infer<typeof IntegrationListSchema> & {
  readonly disconnectingIds?: readonly string[];
  readonly purgingToolkits?: readonly IntegrationToolkit[];
};
export type IntegrationScope = z.infer<typeof scope>;
export type IntegrationToolkit = z.infer<typeof toolkit>;
export interface IntegrationScopeInput {
  toolkit: IntegrationToolkit;
  scope: IntegrationScope;
}
export type IntegrationMutation =
  | ({ action: 'connect'; state: string } & IntegrationScopeInput)
  | ({ action: 'verify'; connectionId: string; sessionUri: string } & IntegrationScopeInput)
  | ({ action: 'confirm' | 'disconnect' } & IntegrationScopeInput)
  | {
      action: 'set-toolkit-policy';
      toolkit: IntegrationToolkit;
      enabled: boolean;
      allowedScopes: 'both' | 'shared_only';
    }
  | { action: 'force-disconnect'; targetConnectionId: string }
  | { action: 'purge-toolkit'; toolkit: IntegrationToolkit; cursor?: string };
export type IntegrationRequest = IntegrationMutation | { action: 'list'; cursor?: string };

export function integrationSettingsKey(organizationId: string, userId: string | undefined) {
  return [...integrationsRootKey(organizationId), userId, 'settings'] as const;
}

export function integrationsRootKey(organizationId?: string | null) {
  return organizationId ? (['integrations', organizationId] as const) : (['integrations'] as const);
}

export function integrationLabelKey(
  organizationId: string | null | undefined,
  userId: string | undefined,
  connection: ConnectedActivityMetadata,
) {
  return [
    ...integrationsRootKey(organizationId),
    userId,
    'label',
    connection.connectionId,
    connection.toolkit,
    connection.accountType,
  ] as const;
}

export function isTerminalIntegrationError(error: unknown): boolean {
  const parsed = z
    .object({
      code: z.enum([
        'integration_identity_mismatch',
        'integration_missing_scopes',
        'integration_forbidden',
      ]),
    })
    .safeParse(error);
  return parsed.success;
}
